const TTL = 30 * 60 * 1000
const canonicalName = (name) =>
  name.replace(/\[entrant\]\[[1-9]\d*\]/, '[entrant][1]')
const isRegion = (name) =>
  /\[(?:region_code|field_\d+_region_code)\]$/.test(name)
const countryName = (name) => name.replace(/region_code\]$/, 'country_code]')

// Only edited customer fields are stored. Hidden checkout/payment/consent inputs never enter this draft.
export class CustomerDraft {
  constructor({ form, names, key, storage, reload, now = Date.now }) {
    this.form = form
    this.names = new Set(names.map(canonicalName))
    this.key = key
    this.storage = storage
    this.now = now
    this.values = {}
    this.countries = {}
    this.pending = new Set()
    this.firstEntrantRevealed = false
    this.at = now()
    this.observedCountries = this.currentCountries()
    try {
      const saved = JSON.parse(storage?.getItem(key) || 'null')
      if (
        reload &&
        saved &&
        Number.isFinite(saved.at) &&
        now() - saved.at < TTL &&
        saved.at <= now() &&
        saved.values &&
        !Array.isArray(saved.values) &&
        typeof saved.values === 'object'
      ) {
        this.at = saved.at
        this.firstEntrantRevealed = saved.firstEntrantRevealed === true
        for (const [name, values] of Object.entries(saved.values)) {
          if (
            this.names.has(canonicalName(name)) &&
            Array.isArray(values) &&
            values.every((value) => typeof value === 'string')
          ) {
            this.values[name] = values
            if (typeof saved.countries?.[name] === 'string')
              this.countries[name] = saved.countries[name]
          }
        }
      } else storage?.removeItem(key)
    } catch {
      /* Storage may be unavailable in embedded/private browsers. */
    }
  }

  eligible(field) {
    return (
      field &&
      this.names.has(canonicalName(field.name || '')) &&
      Array.from(this.form.elements).includes(field) &&
      !field.disabled &&
      !field.matches?.(':disabled') &&
      !field.readOnly &&
      ['INPUT', 'SELECT', 'TEXTAREA'].includes(field.tagName) &&
      ![
        'hidden',
        'file',
        'password',
        'submit',
        'button',
        'reset',
        'image',
      ].includes(field.type)
    )
  }

  fields(name) {
    return Array.from(this.form.elements).filter(
      (field) => field.name === name && this.eligible(field)
    )
  }

  currentCountries() {
    return Object.fromEntries(
      Array.from(this.form.elements)
        .filter(
          (field) => this.eligible(field) && /country_code\]$/.test(field.name)
        )
        .map((field) => [field.name, field.value])
    )
  }

  read(name) {
    return this.fields(name).flatMap((input) => {
      if (['checkbox', 'radio'].includes(input.type))
        return input.checked ? [input.value] : []
      if (input.multiple)
        return Array.from(input.selectedOptions, (option) => option.value)
      return [input.value]
    })
  }

  capture(field) {
    if (!this.eligible(field)) return []
    const changed = [field.name]
    this.values[field.name] = this.read(field.name)
    if (
      /^product_\d+\[entrant\]\[1\]/.test(field.name) &&
      this.presentationField('purchase[show_first_entrant]')?.value === '1'
    ) {
      this.firstEntrantRevealed = true
    }
    if (isRegion(field.name))
      this.countries[field.name] = this.read(countryName(field.name))[0]
    if (/country_code\]$/.test(field.name)) {
      const region = field.name.replace(/country_code\]$/, 'region_code]')
      if (
        this.observedCountries[field.name] !== field.value &&
        this.fields(region).length
      ) {
        for (const input of this.fields(region)) input.value = ''
        this.values[region] = ['']
        this.countries[region] = field.value
        changed.push(region)
      }
      this.observedCountries[field.name] = field.value
    }
    this.at = this.now()
    this.save()
    return changed
  }

  presentationField(name) {
    return Array.from(this.form.elements).find(
      (field) =>
        field.name === name && field.type === 'hidden' && !field.disabled
    )
  }

  reveal(name, action) {
    const flag = this.presentationField(name)
    if (
      !flag ||
      flag.value === '1' ||
      !this.form.querySelector?.(`[data-action$="#${action}"]`)
    )
      return false
    // Derive only these existing presentation flags; never capture or restore arbitrary hidden values.
    flag.value = '1'
    return true
  }

  participantPresent(name) {
    const prefix = name.match(/^product_\d+\[entrant\]\[[1-9]\d*\]/)?.[0]
    return (
      !prefix ||
      Array.from(this.form.elements).some((field) =>
        field.name.startsWith(`${prefix}[`)
      )
    )
  }

  conditionalControl(name, previousCountries) {
    const countryChanged = (country) =>
      this.fields(country).length > 0 &&
      previousCountries[country] !== this.read(country)[0]
    const raw = Array.from(this.form.elements).filter(
      (field) => field.name === name
    )
    if (raw.some((field) => field.readOnly || field.type === 'hidden'))
      return false
    if (
      name === 'purchase[company_name]' &&
      this.presentationField('purchase[show_company_name]')?.value === '1'
    )
      return true
    if (
      /^purchase\[(?:company_name|vat_no|address|address_2|city|postal_code|region_code)\]$/.test(
        name
      )
    )
      return countryChanged('purchase[country_code]')
    const participant = name.match(
      /^(product_\d+\[entrant\]\[([1-9]\d*)\])\[(.+)\](?:\[\])?$/
    )
    if (!participant) return false
    if (
      participant[2] === '1' &&
      this.firstEntrantRevealed &&
      this.presentationField('purchase[show_first_entrant]')?.value === '1'
    )
      return true
    if (participant[3] === 'birth_date')
      return countryChanged('purchase[country_code]')
    if (['gift_at', 'gift_message'].includes(participant[3]))
      return this.fields(`${participant[1]}[gift]`).some(
        (field) => field.type === 'checkbox' && field.checked
      )
    return isRegion(name) && countryChanged(countryName(name))
  }

  restore(onlyNames = null, { reconciled = false } = {}) {
    let restored = false
    if (this.values['purchase[company_name]'])
      restored =
        this.reveal('purchase[show_company_name]', 'showCompanyNameField') ||
        restored
    if (this.firstEntrantRevealed) {
      restored =
        this.reveal('purchase[show_first_entrant]', 'showFirstEntrant') ||
        restored
    }
    const previousCountries = this.currentCountries()
    const previousGifts = new Map(
      Object.keys(this.values)
        .filter((name) => /\[gift\]$/.test(name))
        .map((name) => [name, this.read(name).includes('1')])
    )
    // Restore controlling fields before their dependents, regardless of edit order.
    const priority = (name) =>
      /country_code\]$/.test(name)
        ? -2
        : /\[gift\]$/.test(name)
        ? -1
        : Number(isRegion(name))
    const names = Object.keys(this.values).sort(
      (a, b) => priority(a) - priority(b)
    )
    for (const name of names) {
      const values = this.values[name]
      const fields = this.fields(name)
      if (!fields.length) {
        if (this.participantPresent(name)) {
          if (this.pending.has(name) && !reconciled) continue
          const giftName = name.replace(/\[gift_(?:at|message)\]$/, '[gift]')
          // Revealing participant identity can expose an unchecked gift control first.
          // Its newly restored check needs another response before gift details enable.
          const giftJustEnabled =
            giftName !== name &&
            previousGifts.get(giftName) === false &&
            this.read(giftName).includes('1')
          if (
            (!onlyNames || onlyNames.has(name) || giftJustEnabled) &&
            this.conditionalControl(name, previousCountries)
          ) {
            this.pending.add(name)
            restored = true
            continue
          }
        }
        this.pending.delete(name)
        delete this.values[name]
        delete this.countries[name]
        continue
      }
      const wasPending = this.pending.delete(name)
      if (onlyNames && !onlyNames.has(name) && !wasPending) {
        // The server may normalize an earlier edit. Keep that value for the next reload.
        this.values[name] = this.read(name)
        if (isRegion(name))
          this.countries[name] = this.read(countryName(name))[0]
        continue
      }
      const country = isRegion(name) && countryName(name)
      if (country && this.countries[name] !== this.read(country)[0]) {
        delete this.values[name]
        delete this.countries[name]
        continue
      }
      for (const field of fields) {
        if (['checkbox', 'radio'].includes(field.type))
          field.checked = values.includes(field.value)
        else if (field.multiple) {
          for (const option of field.options)
            option.selected = values.includes(option.value)
        } else {
          const value = values[0] || ''
          if (
            field.tagName === 'SELECT' &&
            value &&
            !Array.from(field.options).some((option) => option.value === value)
          ) {
            // Carry a region into a country reconciliation, never revive a removed choice.
            if (
              !country ||
              previousCountries[country] === this.read(country)[0]
            ) {
              delete this.values[name]
              delete this.countries[name]
              continue
            }
            const option = field.ownerDocument.createElement('option')
            option.value = value
            option.textContent = value
            field.add(option)
          }
          field.value = value
        }
        restored = true
      }
      if (this.values[name]) this.values[name] = this.read(name)
    }
    this.observedCountries = this.currentCountries()
    this.save()
    return restored
  }

  save() {
    try {
      this.storage?.setItem(
        this.key,
        JSON.stringify({
          at: this.at,
          values: this.values,
          countries: this.countries,
          firstEntrantRevealed: this.firstEntrantRevealed,
        })
      )
    } catch {
      /* A draft must never prevent checkout. */
    }
  }
}
